/* eslint-disable arrow-body-style */
import { Box, Flex } from '@everlywell/ui-kit';
import FaqAccordion, {
  FaqAccordionProps,
} from 'components/FaqV2/components/FaqAccordion';
import React from 'react';

import FakeProductDescription from '../components/FakeProductDescription';
import ProductDescription, {
  ProductDescriptionProps,
} from '../components/ProductDescription';
import ProductPromotion, {
  ProductPromotionProps,
} from '../components/ProductPromotion';
import SectionContainer from '../components/SectionContainer';
import ImageCarousel, { ImageCarouselProps } from './components/ImageCarousel';

export type HeroWithProductProps = {
  productDescription?: ProductDescriptionProps | null;
  imageCarousel: ImageCarouselProps;
  productPromotion?: ProductPromotionProps | null;
  productFAQ?: FaqAccordionProps | null;
  hasFakeProductDescription?: boolean;
};

const SLIDE_STYLE = { width: '100%' };
/**
 * HeroWithProduct component. This component is used to display a product description, promotion, FAQ, and image carousel.
 *
 * @param props [HeroWithProductProps]
 * @param props.productDescription [ProductDescriptionProps]
 * @param props.productPromotion [ProductPromotionProps]
 * @param props.productFAQ [FaqAccordionProps]
 * @param props.imageCarousel [ImageCarouselProps]
 * @returns
 */
export default function HeroWithProduct({
  productDescription,
  productPromotion,
  productFAQ,
  imageCarousel,
  hasFakeProductDescription,
}: HeroWithProductProps): JSX.Element {
  return (
    <SectionContainer size={['sm', null, 'xl']} paddingTop="10">
      <Flex
        gap={[4, null, 12]}
        width="100%"
        position="relative"
        flexDirection={['column', 'column', 'row']}
      >
        {/* Sticky and becomes the second column on desktop */}
        <Box width={['100%', '100%', '45%']} order={[0, null, 1]}>
          <Box
            position={[null, null, 'sticky']}
            marginTop={[null, null, 10]}
            top={[null, null, '70px']} // Based on the header height and mdp config
          >
            {hasFakeProductDescription ? (
              <FakeProductDescription />
            ) : (
              <ProductDescription {...productDescription} />
            )}
          </Box>
        </Box>

        <Flex
          width={['100%', '100%', '55%']}
          gap="4"
          alignItems="flex-start"
          flexDirection="column"
        >
          {/* Image Carousel */}
          {imageCarousel?.images?.length > 0 ? (
            <Box width="100%">
              <ImageCarousel {...imageCarousel} slideStyle={SLIDE_STYLE} />
            </Box>
          ) : null}

          {/* Product Promotion */}
          {productPromotion ? <ProductPromotion {...productPromotion} /> : null}

          <Box
            width="100%"
            backgroundColor="tints.lightCream"
            padding={[6, null, 8]}
            borderRadius="xl"
            data-testid="faq-accordion"
          >
            {productFAQ ? (
              <FaqAccordion
                {...productFAQ}
                trackingLabel="HeroWithProduct - FaqAccordion"
                useAccordionButtonTextElement={false}
              />
            ) : null}
          </Box>
        </Flex>
      </Flex>
    </SectionContainer>
  );
}
